// src/lib/lesson-generation.ts
//
// One-shot LLM generation of a topic lesson. Designed to be called from
// the CLI (bulk-seed) and from the lazy-on-first-request handler in the
// /learn/[slug] page.
//
// Output is structured JSON with a stable shape (see LessonContent below).
// We use a strict prompt that asks the model to return JSON only, then
// parse defensively. Failures are reported as null + an error string —
// the lazy handler can show a "lesson not yet available" message and the
// CLI can retry.

import { lessonRenderIssues } from '@/lib/learn/lesson-content-check'
import Anthropic from '@anthropic-ai/sdk'
import { CANONICAL_TOPICS } from '@/lib/sat/taxonomy'
import { honestReadMinutes } from '@/lib/learn/read-time'
import {
  answerLetterFromSolution,
  exampleChoiceIssue,
  normalizeWorkedExample,
} from '@/lib/learn/worked-example-choices'
import { CURRENT_ANTHROPIC_MODELS } from '@/lib/tutor/anthropic-models'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/** A single MCQ choice on a worked example. */
export type WorkedExampleChoice = {
  /** "A" | "B" | "C" | "D". */
  label: 'A' | 'B' | 'C' | 'D'
  /** The choice text (markdown allowed; usually plain). */
  text: string
}

export type WorkedExample = {
  /** Problem stem (markdown OK; usually plain). For MCQ examples, the
   *  stem should NOT include the A/B/C/D choices — those live in
   *  `choices[]` so they can be rendered as clickable options. */
  problem: string
  /** Step-by-step solution (markdown — bullet/numbered lists encouraged). */
  solution: string
  /** One-sentence "what this example teaches you." */
  insight: string

  /* ---- v2 (interactive worked examples) — both optional ---- */

  /** Exactly four choices (A/B/C/D) when this example is an MCQ.
   *  Omit (or leave empty) for SPR / open-ended examples — those render
   *  with a reveal-only button. */
  choices?: WorkedExampleChoice[]
  /** Letter of the correct choice. Required iff `choices` is set with 4
   *  entries. The renderer uses this to score the student's click. */
  correctChoice?: 'A' | 'B' | 'C' | 'D'
}

export type Pitfall = {
  /** Short name of the trap (5-10 words). */
  name: string
  /** Why students fall for it + how to avoid (markdown, 1-3 sentences). */
  explanation: string
}

/**
 * Flashcard for the sidebar + download. 5-10 per lesson typical.
 * Front is a term/prompt/question; back is the definition/formula/answer.
 * Markdown allowed on both sides (KaTeX too: `$x^2$`).
 */
export type Flashcard = { front: string; back: string }

/**
 * Inline diagram referenced from the explanation/example markdown via:
 *   [[diagram:<id>]]
 * The renderer matches the marker against this list. See
 * src/components/lesson/diagrams for supported renderer types.
 */
export type LessonDiagram = {
  /** Stable id used in the marker. Must be unique within a lesson. */
  id: string
  type:
    | 'function-graph'
    | 'number-line'
    | 'sentence-diagram'
    | 'comparison-table'
    | 'decision-tree'
    | 'highlighted-passage'
    | 'right-triangle'
    | 'right-triangle-altitude'
    | 'unit-circle'
    | 'rectangular-prism'
    | 'cylinder'
    | 'circle'
    | 'triangle'
    | 'parallel-lines'
    | 'polygon'
    | 'cone'
    | 'sphere'
    | 'pyramid'
    | 'box-plot'
    | 'dot-plot'
    | 'shape'
  /** Renderer-specific params. Validated by the diagram component. */
  params: Record<string, unknown>
  /** Optional caption shown below the diagram. */
  caption?: string
}

/**
 * Mini-check between sections — a single question from the bank that
 * tests whether the concept just landed. Referenced by question id;
 * fetched lazily at render time. Shows feedback on submit.
 */
export type LessonMiniCheck = {
  /** ID of a Question row in the bank. */
  questionId: string
  /** 1-2 sentence prompt shown above the question. */
  prompt?: string
}

/** Cross-lesson navigation reference. */
export type TopicLink = { topic: string; slug: string }

export type LessonContent = {
  /** 1-2 sentence hook that sets the stakes. Plain text. */
  hook: string
  /** Core explanation, 250-450 words, markdown. Assumes ZERO prior knowledge.
   * May include `[[diagram:<id>]]` markers and KaTeX `$...$` math. */
  explanation: string
  /** 2-3 worked examples. */
  workedExamples: WorkedExample[]
  /** 2-4 common pitfalls. */
  pitfalls: Pitfall[]
  /** 3-5 short bullet takeaways. */
  keyTakeaways: string[]

  /* ---- v2 additions (all optional for backward compat) ---- */

  /** Flashcards for the sidebar + download. 5-10 typical. */
  flashcards?: Flashcard[]
  /** Inline diagrams referenced from markdown via `[[diagram:<id>]]`. */
  diagrams?: LessonDiagram[]
  /** Topics to learn before this one (up to 3). */
  prerequisites?: TopicLink[]
  /** Logical next topics (up to 3). */
  nextTopics?: TopicLink[]
  /** Estimated reading time in minutes (~200 words/min). */
  estimatedReadMinutes?: number
  /** Difficulty hint for the header chip. */
  difficulty?: 'intro' | 'easy' | 'medium' | 'hard'
  /** Mini-check question rendered between explanation and examples. */
  miniCheck?: LessonMiniCheck
}

export type LessonGenerationInput = {
  topic: string
  /** "RW" or "MATH" — used to tune the prompt's tone + content. */
  section: 'RW' | 'MATH' | null
  /** Sample questions on this topic, used as concrete grounding for the LLM. */
  sampleQuestions?: Array<{
    stem: string
    answer: string
    explanation: string | null
  }>
}

export type LessonGenerationResult = {
  content: LessonContent
  rawResponse: string
  modelUsed: string
  /** Token usage for cost tracking. */
  usage: { inputTokens: number; outputTokens: number }
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** Bumped whenever the prompt / output schema changes meaningfully.
 *  v2: workedExamples now carry structured `choices[]` + `correctChoice`
 *      so they can render as scored MCQs instead of reveal-only cards.
 *  v3: prompt now requires the rich fields the UI already supports —
 *      flashcards, inline diagrams (with per-type param specs + [[diagram:id]]
 *      markers), difficulty, and nextTopics. (miniCheck is injected server-side
 *      with a real question id; the model is told not to emit it.)
 *  v4: currency must be written as an ESCAPED dollar sign (`\$45`) — a bare `$`
 *      is parsed as a KaTeX math delimiter by the renderer (remark-math), so an
 *      unescaped `$45 ... $30` silently renders the prose between as math.
 *  v5: function-graph gains the "system" form (`curves` + intersection
 *      `points`), and a caption may only name graphs the figure plots — the
 *      Systems lesson shipped "where they intersect" over a single line. Both
 *      rules are now ENFORCED on the output (lesson-content-check.ts). */
export const LESSON_PROMPT_VERSION = 5

/** Default model for lesson generation: Opus (the registry's top-quality
 *  alias). Lessons are student-facing educational content where quality matters
 *  most, and Opus also emits cleaner, complete JSON (fewer truncation/parse
 *  failures than Sonnet on math-heavy, LaTeX-laden topics). Resolved from the
 *  single model registry rather than a raw string; pass `--model` to override
 *  for a cheaper bulk run. */
export const DEFAULT_LESSON_MODEL: string = CURRENT_ANTHROPIC_MODELS.OPUS

const SYSTEM_PROMPT = `You are an expert SAT/PSAT/ACT instructor writing concept explainers
for high school students preparing for these tests. Your audience is a
student who may have no prior knowledge of the topic.

Your job: write a single topic lesson that gets the student from "I
don't know what this is" to "I can recognize this on the test and try
the questions."

You will respond ONLY with a single JSON object matching this schema:

{
  "hook": "1-2 sentence opening that sets the stakes (why this topic matters on the SAT/etc.). Plain text, no markdown.",
  "explanation": "The core concept explained from scratch. 250-450 words. Markdown allowed (use **bold** for key terms, lists, and \`backticks\` for variables/formulas/symbols). Assume ZERO prior knowledge — define every term the first time it appears. Be conversational, not academic.",
  "workedExamples": [
    {
      "problem": "Just the question STEM (the passage / setup / what's being asked). Do NOT include the A/B/C/D choices in this string for MCQ examples — those go in the choices array below.",
      "choices": [
        { "label": "A", "text": "..." },
        { "label": "B", "text": "..." },
        { "label": "C", "text": "..." },
        { "label": "D", "text": "..." }
      ],
      "correctChoice": "C",
      "solution": "Step-by-step solution. Markdown with numbered steps. Show the reasoning, not just the arithmetic. Define every step's purpose. End by naming the correct letter so students see WHY it's right.",
      "insight": "One sentence: 'this example shows you that ___'"
    }
    // 2 or 3 worked examples total; progress easier → harder.
    // For SPR / open-ended (numeric-input) examples, OMIT both
    // "choices" and "correctChoice" — the renderer will show a
    // reveal-only card. For MCQ examples, you MUST include a 4-entry
    // choices array (labels exactly "A","B","C","D" in order) AND a
    // correctChoice letter.
  ],
  "pitfalls": [
    {
      "name": "Short name of the trap (5-10 words).",
      "explanation": "Why students fall for this + how to avoid. 1-3 sentences. Markdown OK."
    }
    // 2-4 pitfalls total. Be specific to the topic — generic pitfalls
    // ('read carefully') are unhelpful.
  ],
  "keyTakeaways": [
    "Bullet 1 — short, declarative, specific to this topic.",
    "Bullet 2",
    "Bullet 3"
    // 3-5 takeaways total
  ],
  "difficulty": "one of: intro | easy | medium | hard — how demanding this topic typically is on the test",
  "flashcards": [
    { "front": "Term, formula, or question. Markdown + KaTeX ($...$) allowed.", "back": "Definition / formula / answer. Markdown + KaTeX allowed." }
    // REQUIRED: 6-8 flashcards covering the key terms, formulas, and rules of this topic.
  ],
  "diagrams": [
    { "id": "fig1", "type": "<one allowed type below>", "params": { }, "caption": "Short caption." }
    // REQUIRED: 2-4 diagrams that genuinely aid understanding. Reference EACH one
    // from the explanation (or a worked example solution) with a marker on its own
    // line: [[diagram:fig1]]. Every id here MUST have a matching marker and vice-versa.
  ],
  "nextTopics": [
    { "topic": "Exact name of a related topic to study next" }
    // 1-3 topics, chosen ONLY from the canonical topic list in the user message.
  ]
}

DIAGRAMS — use ONLY these types with EXACTLY these params. If no diagram genuinely fits a point, omit it rather than invent a type or fabricate params. Use math diagrams for Math topics and the reading/writing diagrams for R&W topics.

Math:
- function-graph — a line or parabola on a grid.
    linear:    { "kind": "linear", "slope": 2, "intercept": 1, "xMin": -2, "xMax": 8, "yMin": -2, "yMax": 12, "points": [{ "x": 1, "y": 3, "label": "(1, 3)" }, { "x": 5, "y": 11, "label": "(5, 11)" }], "showRiseRun": true }
    quadratic: { "kind": "quadratic", "a": 1, "b": 0, "c": -4, "xMin": -4, "xMax": 4, "yMin": -6, "yMax": 6 }
    system (two graphs and where they meet): the first via the fields above, every other one in "curves", and the meeting point(s) in "points":
               { "kind": "linear", "slope": -1, "intercept": 14, "curves": [{ "kind": "linear", "slope": 1, "intercept": -6 }], "points": [{ "x": 10, "y": 4, "label": "(10, 4)" }], "xMin": 0, "xMax": 14, "yMin": -2, "yMax": 14 }
    A caption may only name what the figure plots: every equation a caption names must be drawn, and a caption that says lines intersect / cross / meet needs at least two graphs. A figure that shows one line under a caption about two is rejected.
- number-line — values / inequality solution sets.
    { "min": -5, "max": 10, "marks": [-3, 0, 5], "interval": { "from": 2, "to": 7, "open": false } }   // open:true = strict (< or >); open:false = inclusive (<= or >=)
- right-triangle — Pythagoras, special triangles, basic trig.
    { "base": 3, "height": 4, "baseLabel": "3", "heightLabel": "4", "hypLabel": "5", "showRightAngle": true }
- unit-circle — trig on the unit circle.
    { "angleDeg": 30, "showSinCos": true, "pointLabel": "(cos 30, sin 30)" }
- rectangular-prism — box volume / surface area.
    { "length": 5, "width": 3, "height": 4, "lengthLabel": "5", "widthLabel": "3", "heightLabel": "4" }
- cylinder — cylinder volume / surface area.
    { "radius": 3, "height": 7, "radiusLabel": "3", "heightLabel": "7" }

Reading & Writing:
- comparison-table — side-by-side categories (e.g. transition types).
    { "title": "Transition types", "headers": ["Relationship", "Words", "Example"], "rows": [["Contrast", "however", "Sales rose; however, profits fell."], ["Addition", "moreover", "It is cheap; moreover, it is fast."]] }
- sentence-diagram — label the parts of a sentence.
    { "segments": [{ "text": "Maria,", "kind": "subject" }, { "text": "who studies biology", "kind": "nonrestrictive" }, { "text": ", leads the team.", "kind": "predicate" }] }
    // kind is one of: subject | predicate | independent | nonrestrictive | highlight | punctuation | fragment
- highlighted-passage — highlight signal words in a short passage.
    { "text": "Researchers assumed the trend was universal. However, it held only when noted in advance.", "highlights": [{ "phrase": "However", "color": "amber", "label": "contrast" }, { "phrase": "only when", "color": "cyan", "label": "condition" }] }
    // color is one of: cyan | amber | emerald | rose
- decision-tree — a yes/no rule chain (e.g. semicolon vs comma). Keep depth <= 3.
    { "root": { "question": "Are both sides complete sentences?", "yes": { "answer": "Use a semicolon" }, "no": { "answer": "Use a comma" } } }
    // Every node is EITHER { "question": str, "yes": <node>, "no": <node> } OR { "answer": str } — never both, never partial.

Do NOT emit a "miniCheck" field — the app wires that to a real practice question.

Style guide:
- Conversational and warm, but not condescending.
- Use concrete examples over abstract definitions where possible.
- For Math topics: show the math. Define notation. Don't skip steps.
- For Reading & Writing topics: explain the underlying logic of the
  question type, not just "look for X". Reference passage clues
  students should look for.
- Avoid filler ("Great question!" / "It's important to remember..." /
  "As you know..."). Get to the substance fast.
- Don't reference 'the SAT' or 'the College Board' constantly — say
  'the test' or 'these questions.'

CURRENCY — CRITICAL: the renderer treats a bare \`$\` as a math delimiter
(KaTeX). Always write money with an ESCAPED dollar sign: \`\\$45\`, \`\\$2,300\`,
\`\\$12.50\` — NEVER a bare \`$45\`. A bare \`$\` in "costs $4 each ... spends $48"
makes everything between the two signs render as broken math. Reserve unescaped
\`$...$\` strictly for actual math expressions (e.g. \`$3x + 2y = 6$\`, \`$f(x)$\`).

If grounded sample questions are provided, use them to calibrate the
difficulty/format of your worked examples — don't paste them verbatim,
but match the style and challenge level.`

/* ------------------------------------------------------------------ */
/*  Prompt builder                                                     */
/* ------------------------------------------------------------------ */

export function buildLessonUserMessage(input: LessonGenerationInput): string {
  const lines: string[] = []
  lines.push(`Write a topic lesson for: **${input.topic}**`)
  if (input.section) {
    lines.push(`Section: ${input.section === 'MATH' ? 'Math' : 'Reading & Writing'}`)
  }

  if (input.sampleQuestions && input.sampleQuestions.length > 0) {
    lines.push('')
    lines.push(
      'Here are sample questions from this topic in our bank — use them to calibrate the format and difficulty of your worked examples (DO NOT paste verbatim):',
    )
    for (let i = 0; i < input.sampleQuestions.length; i++) {
      const q = input.sampleQuestions[i]
      lines.push('')
      lines.push(`SAMPLE ${i + 1}:`)
      lines.push(`Stem: ${q.stem.slice(0, 800)}`)
      lines.push(`Answer: ${q.answer}`)
      if (q.explanation) lines.push(`Explanation: ${q.explanation.slice(0, 400)}`)
    }
  }

  // Canonical topic list so nextTopics links resolve to real lessons (the
  // parser slugifies the chosen names). Pick related ones from this list only.
  lines.push('')
  lines.push('Choose "nextTopics" ONLY from this canonical topic list (use the exact names):')
  lines.push(CANONICAL_TOPICS.map((t) => `  - ${t}`).join('\n'))

  lines.push('')
  lines.push(
    'Return ONLY the JSON object described in your instructions. No prose, no markdown fences.',
  )
  return lines.join('\n')
}

/* ------------------------------------------------------------------ */
/*  Response parser                                                    */
/* ------------------------------------------------------------------ */

/**
 * A decision-tree node is EITHER a leaf `{ answer }` or a branch
 * `{ question, yes, no }` where yes/no are themselves valid nodes. The
 * DecisionTree renderer recurses without guarding, so we validate the whole
 * tree here (depth-capped) and drop the diagram if it's malformed.
 */
function isValidDecisionNode(n: unknown, depth = 0): boolean {
  if (depth > 6 || !n || typeof n !== 'object' || Array.isArray(n)) return false
  const o = n as Record<string, unknown>
  if (typeof o.answer === 'string' && o.answer.trim()) return true
  if (typeof o.question === 'string' && o.question.trim()) {
    return isValidDecisionNode(o.yes, depth + 1) && isValidDecisionNode(o.no, depth + 1)
  }
  return false
}

/**
 * Defensive parser. Returns null on unparseable input. Coerces missing
 * fields to safe defaults (empty arrays, empty strings) rather than
 * throwing — a partial lesson is better than no lesson while we iterate
 * the prompt.
 */
export function parseLessonResponse(rawText: string): LessonContent | null {
  if (!rawText || typeof rawText !== 'string') return null
  let text = rawText.trim()

  // Strip ```json fences if present
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/)
  if (fence) text = fence[1].trim()

  // Slice between first { and last } in case there's prose around the JSON
  const first = text.indexOf('{')
  const last = text.lastIndexOf('}')
  if (first === -1 || last === -1 || last <= first) return null
  const jsonSlice = text.slice(first, last + 1)

  let raw: unknown
  try {
    raw = JSON.parse(jsonSlice)
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>

  const hook = typeof obj.hook === 'string' ? obj.hook.trim() : ''
  const explanation = typeof obj.explanation === 'string' ? obj.explanation.trim() : ''

  const workedExamples: WorkedExample[] = []
  if (Array.isArray(obj.workedExamples)) {
    for (const e of obj.workedExamples) {
      if (!e || typeof e !== 'object') continue
      const x = e as Record<string, unknown>
      const problem = typeof x.problem === 'string' ? x.problem.trim() : ''
      const solution = typeof x.solution === 'string' ? x.solution.trim() : ''
      const insight = typeof x.insight === 'string' ? x.insight.trim() : ''
      if (!problem || !solution) continue

      // v2 optional MCQ fields. We only accept choices when there are
      // exactly four valid {label,text} entries (A/B/C/D, all present)
      // AND a matching correctChoice — otherwise we drop them so the
      // renderer falls back to legacy reveal-only mode rather than
      // rendering a half-broken interactive card.
      let choices: WorkedExampleChoice[] | undefined
      let correctChoice: WorkedExample['correctChoice']
      if (Array.isArray(x.choices) && x.choices.length === 4) {
        const expected: Array<'A' | 'B' | 'C' | 'D'> = ['A', 'B', 'C', 'D']
        const parsed: WorkedExampleChoice[] = []
        for (let i = 0; i < 4; i++) {
          const c = x.choices[i]
          if (!c || typeof c !== 'object') break
          const cx = c as Record<string, unknown>
          const label = typeof cx.label === 'string' ? cx.label.trim().toUpperCase() : ''
          const text = typeof cx.text === 'string' ? cx.text.trim() : ''
          if (label !== expected[i] || !text) break
          parsed.push({ label: expected[i], text })
        }
        if (parsed.length === 4) {
          const given =
            typeof x.correctChoice === 'string' ? x.correctChoice.trim().toUpperCase() : ''
          // A missing or garbled correctChoice is recoverable when the
          // solution names exactly one letter.
          const cc =
            given === 'A' || given === 'B' || given === 'C' || given === 'D'
              ? given
              : answerLetterFromSolution(solution)
          if (cc) {
            choices = parsed
            correctChoice = cc
          }
        }
      }

      // Choices written inline in the problem become real choices; and an
      // example that still asks about choices it doesn't have is DROPPED,
      // never kept as reveal-only — that shipped Boundaries examples asking
      // "Which choice…?" over no choices (worked-example-choices.ts).
      const example = normalizeWorkedExample({ problem, solution, insight, choices, correctChoice })
      if (exampleChoiceIssue(example)) continue
      workedExamples.push(example)
    }
  }

  const pitfalls: Pitfall[] = []
  if (Array.isArray(obj.pitfalls)) {
    for (const p of obj.pitfalls) {
      if (!p || typeof p !== 'object') continue
      const x = p as Record<string, unknown>
      const name = typeof x.name === 'string' ? x.name.trim() : ''
      const expl = typeof x.explanation === 'string' ? x.explanation.trim() : ''
      if (!name && !expl) continue
      pitfalls.push({ name, explanation: expl })
    }
  }

  const keyTakeaways: string[] = []
  if (Array.isArray(obj.keyTakeaways)) {
    for (const t of obj.keyTakeaways) {
      if (typeof t === 'string' && t.trim()) keyTakeaways.push(t.trim())
    }
  }

  // Sanity check: a lesson with no explanation is broken; bail.
  if (!explanation || workedExamples.length === 0) return null

  /* ---- v2 optional fields ---- */

  const flashcards: Flashcard[] = []
  if (Array.isArray(obj.flashcards)) {
    for (const f of obj.flashcards) {
      if (!f || typeof f !== 'object') continue
      const x = f as Record<string, unknown>
      const front = typeof x.front === 'string' ? x.front.trim() : ''
      const back = typeof x.back === 'string' ? x.back.trim() : ''
      if (!front || !back) continue
      flashcards.push({ front, back })
    }
  }

  const VALID_DIAGRAM_TYPES = [
    'function-graph',
    'number-line',
    'sentence-diagram',
    'comparison-table',
    'decision-tree',
    'highlighted-passage',
    // Geometry/trig renderers exist + are dispatched (Diagram.tsx) and degrade
    // gracefully on bad params — enabled so geometry lessons can use them.
    'right-triangle',
    'right-triangle-altitude',
    'unit-circle',
    'rectangular-prism',
    'cylinder',
    'circle',
    'triangle',
    'parallel-lines',
    'polygon',
    'cone',
    'sphere',
    'pyramid',
    'box-plot',
    'dot-plot',
    'shape',
  ] as const
  const diagrams: LessonDiagram[] = []
  if (Array.isArray(obj.diagrams)) {
    for (const d of obj.diagrams) {
      if (!d || typeof d !== 'object') continue
      const x = d as Record<string, unknown>
      const id = typeof x.id === 'string' ? x.id.trim() : ''
      const type = typeof x.type === 'string' ? x.type : ''
      if (!id || !(VALID_DIAGRAM_TYPES as ReadonlyArray<string>).includes(type)) continue
      const params =
        x.params && typeof x.params === 'object' && !Array.isArray(x.params)
          ? (x.params as Record<string, unknown>)
          : {}
      // decision-tree is the one renderer that can throw on a malformed/missing
      // `root` (every other diagram guards + defaults). Drop it unless the root
      // is a well-formed node tree, so an LLM slip can't crash the lesson page.
      if (type === 'decision-tree' && !isValidDecisionNode(params.root)) continue
      const caption = typeof x.caption === 'string' ? x.caption : undefined
      diagrams.push({ id, type: type as LessonDiagram['type'], params, caption })
    }
  }

  function parseLinks(raw: unknown): TopicLink[] {
    if (!Array.isArray(raw)) return []
    const out: TopicLink[] = []
    for (const t of raw) {
      if (!t || typeof t !== 'object') continue
      const x = t as Record<string, unknown>
      const topic = typeof x.topic === 'string' ? x.topic.trim() : ''
      if (!topic) continue
      const slug = typeof x.slug === 'string' && x.slug.trim() ? x.slug.trim() : slugifyTopic(topic)
      out.push({ topic, slug })
    }
    return out
  }
  const prerequisites = parseLinks(obj.prerequisites).slice(0, 3)
  const nextTopics = parseLinks(obj.nextTopics).slice(0, 3)

  // MEASURED, never the model's guess. This used to trust
  // `obj.estimatedReadMinutes` whenever the model supplied one, and the model
  // reliably said "2" — which is how 79 lessons shipped claiming a two-minute
  // read for well over a thousand words, and why every one of them failed the
  // review checklist's reading-time item. The old fallback was wrong too: it
  // counted only `explanation`, so it ignored the worked examples, pitfalls and
  // takeaways a student also reads. honestReadMinutes counts the whole page,
  // and the review check calls the same function, so a lesson cannot be
  // generated in a state its own reviewer would reject.
  const estimatedReadMinutes = honestReadMinutes({
    hook,
    explanation,
    workedExamples,
    pitfalls,
    keyTakeaways,
  })

  const VALID_DIFFICULTY = ['intro', 'easy', 'medium', 'hard'] as const
  let difficulty: LessonContent['difficulty']
  if (
    typeof obj.difficulty === 'string' &&
    (VALID_DIFFICULTY as ReadonlyArray<string>).includes(obj.difficulty)
  ) {
    difficulty = obj.difficulty as LessonContent['difficulty']
  }

  // Mini-check (optional)
  let miniCheck: LessonMiniCheck | undefined
  if (obj.miniCheck && typeof obj.miniCheck === 'object' && !Array.isArray(obj.miniCheck)) {
    const mc = obj.miniCheck as Record<string, unknown>
    if (typeof mc.questionId === 'string' && mc.questionId.length > 0) {
      miniCheck = {
        questionId: mc.questionId,
        prompt: typeof mc.prompt === 'string' ? mc.prompt : undefined,
      }
    }
  }

  const lesson: LessonContent = {
    hook,
    explanation,
    workedExamples,
    pitfalls,
    keyTakeaways,
    flashcards: flashcards.length > 0 ? flashcards : undefined,
    diagrams: diagrams.length > 0 ? diagrams : undefined,
    prerequisites: prerequisites.length > 0 ? prerequisites : undefined,
    nextTopics: nextTopics.length > 0 ? nextTopics : undefined,
    estimatedReadMinutes,
    difficulty,
    miniCheck,
  }

  // A lesson that would render wrong is rejected, not stored: a bare `$` amount
  // that remark-math pairs into a math span swallowing the prose, or a figure
  // whose caption names equations / lines meeting that it doesn't plot
  // (src/lib/learn/lesson-content-check.ts). The caller retries generation.
  if (lessonRenderIssues(lesson).length > 0) return null
  return lesson
}

/* ------------------------------------------------------------------ */
/*  Slug helper                                                        */
/* ------------------------------------------------------------------ */

/**
 * URL-safe slug for a topic. Stable so we can derive the lesson slug
 * from a topic at any time without persisting it.
 *
 *   "Linear Functions"        → "linear-functions"
 *   "Words in Context"        → "words-in-context"
 *   "Area, volume, and triangles" → "area-volume-and-triangles"
 */
export function slugifyTopic(topic: string): string {
  return topic
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

/* ------------------------------------------------------------------ */
/*  Top-level: generateLesson                                          */
/* ------------------------------------------------------------------ */

export async function generateLesson(
  client: Anthropic,
  input: LessonGenerationInput,
  options: { model?: string; maxTokens?: number } = {},
): Promise<LessonGenerationResult | null> {
  const model = options.model ?? DEFAULT_LESSON_MODEL
  // v3 lessons are rich (flashcards + diagrams + multiple worked examples), so
  // a low ceiling truncates them — the model drops optional sections or emits
  // invalid (truncated) JSON. The content-heaviest topics overflowed 8192, so
  // give generous headroom (a ceiling, not a target — billed on actual output).
  const maxTokens = options.maxTokens ?? 16384

  const response = await client.messages.create({
    model,
    max_tokens: maxTokens,
    system: SYSTEM_PROMPT,
    messages: [{ role: 'user', content: buildLessonUserMessage(input) }],
  })

  const rawResponse = response.content
    .filter((b): b is Anthropic.TextBlock => b.type === 'text')
    .map((b) => b.text)
    .join('\n')

  const parsed = parseLessonResponse(rawResponse)
  if (!parsed) return null

  return {
    content: parsed,
    rawResponse,
    modelUsed: model,
    usage: {
      inputTokens: response.usage.input_tokens ?? 0,
      outputTokens: response.usage.output_tokens ?? 0,
    },
  }
}
